import React, { type PropsWithChildren, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Dialog,
  Portal,
} from "@chakra-ui/react";
import type { CheckedChangeDetails } from "@zag-js/checkbox";

import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LanguageSwitcher } from "../../../language-switcher/index";
import { useTheme } from "@emotion/react";

const DialogPositioner = Dialog.Positioner as React.FC<PropsWithChildren>;
const DialogContent = Dialog.Content as React.FC<
  PropsWithChildren<{ mb?: string }>
>;
const CheckboxGroupRoot = CheckboxGroup as React.FC<
  PropsWithChildren<{ value: string[] }>
>;
const CheckboxControl = Checkbox.Control as React.FC<PropsWithChildren>;
const CheckboxLabel = Checkbox.Label as React.FC<PropsWithChildren>;

type Cookie =
  | typeof COOKIE_CONSENTDATE
  | typeof COOKIE_NECESSARY
  | typeof COOKIE_PREFERENCE
  | typeof COOKIE_STATISTICAL
  | typeof COOKIE_ADS;

export const COOKIE_CONSENTDATE = "mtb_consentDate";
const COOKIE_NECESSARY = "mtb_NecessaryCookies";
const COOKIE_PREFERENCE = "mtb_PreferenceCookies";
const COOKIE_STATISTICAL = "mtb_StatisticalCookies";
const COOKIE_ADS = "mtb_AdCookies";

const allCookiesTypes: Cookie[] = [
  COOKIE_CONSENTDATE,
  COOKIE_NECESSARY,
  COOKIE_PREFERENCE,
  COOKIE_STATISTICAL,
  COOKIE_ADS,
];

export const CookieConsent = () => {
  const [cookies, setCookie, removeCookie] = useCookies(allCookiesTypes);
  const [selectedCookies, setSelectedCookies] = useState<Cookie[]>([]);

  const nextYear = new Date();
  nextYear.setFullYear(new Date().getFullYear() + 1);
  const consentOptions = { path: "/", expires: nextYear };

  const { t } = useTranslation("common");
  const theme = useTheme();
  useEffect(() => {
    setSelectedCookies([
      ...(Object.keys(cookies) as Cookie[]),
      COOKIE_NECESSARY,
    ]);
  }, []);

  const removeAllCookies = () => {
    Object.keys(cookies).map((cookie) => {
      removeCookie(cookie as Cookie);
    });
  };

  const concentSelected = () => {
    removeAllCookies();
    setCookie(COOKIE_CONSENTDATE, new Date(), consentOptions);

    selectedCookies.map((cookie) => {
      setCookie(cookie as Cookie, true, consentOptions);
    });
  };

  const concentAll = () => {
    removeAllCookies();
    setSelectedCookies(allCookiesTypes);

    setCookie(COOKIE_CONSENTDATE, new Date(), consentOptions);
    setCookie(COOKIE_NECESSARY, true, consentOptions);
    setCookie(COOKIE_PREFERENCE, true, consentOptions);
    setCookie(COOKIE_STATISTICAL, true, consentOptions);
    setCookie(COOKIE_ADS, true, consentOptions);
  };

  const setCookieChecked = (cookie: Cookie, checked: boolean) => {
    if (checked) {
      if (!selectedCookies.includes(cookie)) {
        setSelectedCookies([...selectedCookies, cookie]);
      }
    } else {
      setSelectedCookies(selectedCookies.filter((c) => c !== cookie));
    }
  };

  const handleCheckboxChange =
    (cookie: Cookie) => (details: CheckedChangeDetails) => {
      setCookieChecked(cookie, details.checked === true);
    };

  return (
    <Dialog.Root
      open={!cookies[COOKIE_CONSENTDATE]}
      placement="center"
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) {
          return;
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <DialogPositioner>
          <DialogContent mb="4">
            <Dialog.Header display="flex" justifyContent="space-between">
              {t("cookie.title")}
              <LanguageSwitcher />
            </Dialog.Header>
            <Dialog.Body>
              <Box mb="8">
                {t("cookie.subtitle")}
                <Link to="/about" color={theme.colors.primary}>
                  <b> {t("cookie.linkText")}.</b>
                </Link>
              </Box>
              <CheckboxGroupRoot value={selectedCookies}>
                <Checkbox.Root
                  value={COOKIE_NECESSARY as Cookie}
                  checked={selectedCookies.includes(COOKIE_NECESSARY)}
                  disabled
                  mb="4"
                  display="flex"
                  alignItems="baseline"
                  onCheckedChange={handleCheckboxChange(COOKIE_NECESSARY)}
                >
                  <Checkbox.HiddenInput />
                  <CheckboxControl>
                    <Checkbox.Indicator />
                  </CheckboxControl>
                  <CheckboxLabel>
                    <b> {t("cookie.necessary")}</b> {t("cookie.necessarydescr")}
                  </CheckboxLabel>
                </Checkbox.Root>
                <Checkbox.Root
                  value={COOKIE_PREFERENCE as Cookie}
                  checked={selectedCookies.includes(COOKIE_PREFERENCE)}
                  colorPalette="teal"
                  display="flex"
                  alignItems="baseline"
                  mb="4"
                  onCheckedChange={handleCheckboxChange(COOKIE_PREFERENCE)}
                >
                  <Checkbox.HiddenInput />
                  <CheckboxControl>
                    <Checkbox.Indicator />
                  </CheckboxControl>
                  <CheckboxLabel>
                    <span>
                      <b> {t("cookie.preference")}</b>{" "}
                      {t("cookie.preferencedescr")}
                    </span>
                  </CheckboxLabel>
                </Checkbox.Root>
                <Checkbox.Root
                  value={COOKIE_STATISTICAL as Cookie}
                  checked={selectedCookies.includes(COOKIE_STATISTICAL)}
                  colorPalette="teal"
                  mb="4"
                  display="flex"
                  alignItems="baseline"
                  onCheckedChange={handleCheckboxChange(COOKIE_STATISTICAL)}
                >
                  <Checkbox.HiddenInput />
                  <CheckboxControl>
                    <Checkbox.Indicator />
                  </CheckboxControl>
                  <CheckboxLabel>
                    <b> {t("cookie.statistical")}</b>{" "}
                    {t("cookie.statisticaldescr")}
                  </CheckboxLabel>
                </Checkbox.Root>
                <Checkbox.Root
                  value={COOKIE_ADS as Cookie}
                  checked={selectedCookies.includes(COOKIE_ADS)}
                  colorPalette="teal"
                  mb="4"
                  display="flex"
                  alignItems="baseline"
                  onCheckedChange={handleCheckboxChange(COOKIE_ADS)}
                >
                  <Checkbox.HiddenInput />
                  <CheckboxControl>
                    <Checkbox.Indicator />
                  </CheckboxControl>
                  <CheckboxLabel>
                    <b> {t("cookie.advertising")}</b>{" "}
                    {t("cookie.advertisingdescr")}
                  </CheckboxLabel>
                </Checkbox.Root>
              </CheckboxGroupRoot>
            </Dialog.Body>
            <Dialog.Footer>
              <Button
                onClick={concentSelected}
                variant="plain"
                color={theme.colors.primary}
                _hover={{
                  textDecoration: "none",
                }}
                size="sm"
                mr="4"
              >
                {t("cookie.savepref")}
              </Button>
              <Button
                onClick={concentAll}
                backgroundColor={theme.colors.primary}
                color="white"
                variant="solid"
              >
                {t("cookie.saveall")}
              </Button>
            </Dialog.Footer>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </Dialog.Root>
  );
};
